import { memo, useEffect } from "react";
import { useTranslate } from "helpers";
import { validateYupWithFeedback } from "services/forms/validation/feedback";
import { useHandleTextFieldChange, useHandleDateChange } from "services/forms/hooks";
import { NotificationEntityType, NotificationScope } from "services/api/notifications/types";
import { DatePicker } from "ui/components/DatePicker";
import { NOTIFICATION_MESSAGE_SETUP_SCHEMA } from "../validation";
import { StepProps } from "../types";
import ScopeSelector from "./ScopeSelector";
import ChannelSelector from "./ChannelSelector";
import MessageContentSection from "./MessageContentSection";

// Drugi korak — tip notifikacije, kanali i sadržaj poruke. Grupa je odabrana na prvom
// koraku, pa se ovdje podrazumijeva.
const StepMessageSetup: React.FC<StepProps> = ({
  formValues,
  setFormValues,
  errors,
  setErrors,
  registerValidator,
}) => {
  const { t } = useTranslate();
  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  useEffect(() => {
    if (registerValidator) {
      registerValidator(() =>
        validateYupWithFeedback(formValues, NOTIFICATION_MESSAGE_SETUP_SCHEMA, setErrors)
      );
    }
  }, [formValues]);

  const handleScopeChange = (scope: NotificationScope) => {
    setFormValues((prev) => ({
      ...prev,
      scope,
      ...(scope === NotificationScope.Group ? { includedEntityIds: [] } : {}),
    }));
    setErrors(({ scope: _, ...rest }) => rest);
  };

  const handleChannelToggle = (channelValue: number) => {
    const current = formValues.channels;
    const newChannels = current & channelValue
      ? current & ~channelValue
      : current | channelValue;
    setFormValues((prev) => ({ ...prev, channels: newChannels }));
    if (newChannels > 0) {
      setErrors(({ channels, ...rest }) => rest);
    }
  };

  // Prilagođena notifikacija nema entitet s datumom isteka, pa datum slanja bira korisnik.
  const showScope = !formValues.isCustom;
  const showMessage = formValues.channels > 0;
  const showDatePicker = showMessage && formValues.entityType === NotificationEntityType.Custom;

  return (
    <div className="flex justify-center h-full px-4 mt-[100px]">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Postavke obavijesti</h1>
          <p className="text-sm text-slate-600">
            Odredite kome i kako se šalje te što poruka piše.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {showScope && (
            <ScopeSelector
              scope={formValues.scope}
              entityType={formValues.entityType}
              error={errors.scope}
              onChange={handleScopeChange}
            />
          )}

          <ChannelSelector
            channels={formValues.channels}
            error={errors.channels}
            onToggle={handleChannelToggle}
          />

          {showMessage && (
            <MessageContentSection
              title={formValues.title}
              messageTemplate={formValues.messageTemplate}
              errors={errors}
              onFieldChange={handleTextFieldChange}
            />
          )}

          {showDatePicker && (
            <DatePicker
              name="customScheduledAt"
              label={t("admin.notification.field.customScheduledAt")}
              value={formValues.customScheduledAt}
              onChange={handleDateChange}
              width="full"
              cssClassName="bg-white"
              required
              error={errors.customScheduledAt}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(StepMessageSetup);
